import { ACCEPTED_IMAGE_TYPES, IMAGE_CONFIG } from './assetTypes'

export interface ImageInfo {
  mimeType: string
  size: number
  width: number
  height: number
}

export interface ImageProcessingPolicy {
  masterMimeType: 'image/webp'
  masterQuality: number
  masterMinQuality: number
  masterQualityStep: number
  masterMaxDimension: number
  masterMinTargetDimension: number
  masterDimensionStep: number
  masterTargetBytes: number
  masterMaxBytes: number
  thumbnailMimeType: 'image/webp'
  thumbnailQuality: number
  /** A thumbnail is sized by its width (cards show pictures across their width), its height only capped: a tall screenshot stays sharp. */
  thumbnailMaxWidth: number
  thumbnailMaxHeight: number
}

const EXTENSION_MIME_TYPES: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',
}

export function normalizedImageMimeType(file: Pick<File, 'name' | 'type'>): string | null {
  const normalizedType = file.type.toLowerCase()
  if ((ACCEPTED_IMAGE_TYPES as readonly string[]).includes(normalizedType)) return normalizedType
  const extension = file.name.split('.').pop()?.toLowerCase() ?? ''
  return EXTENSION_MIME_TYPES[extension] ?? null
}

export function validateImageFile(file: File): string | null {
  if (!normalizedImageMimeType(file)) return 'Choose a JPEG, PNG, WebP, HEIC, or HEIF image.'
  if (file.size <= 0) return 'This image is empty.'
  if (file.size > IMAGE_CONFIG.clientInputSafetyLimitBytes) {
    return 'This image is larger than the 25 MB safety limit.'
  }
  return null
}

export function getImageProcessingPolicy(info: ImageInfo): ImageProcessingPolicy {
  const screenshot = info.mimeType === 'image/png'

  return {
    masterMimeType: 'image/webp',
    masterQuality: screenshot
      ? IMAGE_CONFIG.master.screenshotWebpQuality
      : IMAGE_CONFIG.master.webpQuality,
    masterMinQuality: screenshot
      ? IMAGE_CONFIG.master.minScreenshotWebpQuality
      : IMAGE_CONFIG.master.minWebpQuality,
    masterQualityStep: IMAGE_CONFIG.master.qualityStep,
    masterMaxDimension: IMAGE_CONFIG.master.maxDimension,
    masterMinTargetDimension: IMAGE_CONFIG.master.minTargetDimension,
    masterDimensionStep: IMAGE_CONFIG.master.dimensionStep,
    masterTargetBytes: IMAGE_CONFIG.master.targetBytes,
    masterMaxBytes: IMAGE_CONFIG.master.maxBytes,
    thumbnailMimeType: 'image/webp',
    thumbnailQuality: IMAGE_CONFIG.thumbnail.webpQuality,
    thumbnailMaxWidth: IMAGE_CONFIG.thumbnail.maxWidth,
    thumbnailMaxHeight: IMAGE_CONFIG.thumbnail.maxHeight,
  }
}
